import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader2, ShieldCheck, AlertTriangle } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function GoogleAuthCallback() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const processed = useRef(false);
  const [state, setState] = useState({ status: "processing", message: "Google oturumu doğrulanıyor..." });

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const hash = window.location.hash || "";
    const params = new URLSearchParams(hash.startsWith("#") ? hash.slice(1) : hash);
    const sessionId = params.get("session_id");

    if (!sessionId) {
      setState({ status: "error", message: "Geçerli bir Google oturum kimliği bulunamadı." });
      return;
    }

    (async () => {
      try {
        const { data } = await api.post("/auth/google/session", { session_id: sessionId });
        // Clean hash from URL immediately
        window.history.replaceState(null, "", window.location.pathname);
        if (data.status === "authenticated") {
          if (data.access_token) localStorage.setItem("gacrf_token", data.access_token);
          await refresh();
          navigate("/dashboard", { replace: true });
          return;
        }
        // Pending or non-approved
        setState({
          status: "pending",
          message: data.message || "Hesabınız SuperAdmin onayı bekliyor.",
        });
      } catch (e) {
        window.history.replaceState(null, "", window.location.pathname);
        setState({ status: "error", message: formatApiError(e) || "Google girişi başarısız oldu." });
      }
    })();
  }, [navigate, refresh]);

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-[#050914]" data-testid="google-auth-callback">
      <div className="w-full max-w-md card p-8 card-accent">
        {state.status === "processing" && (
          <div className="flex flex-col items-center text-center gap-4" data-testid="oauth-processing">
            <Loader2 size={28} className="text-[#00E5FF] animate-spin" />
            <h2 className="font-heading text-lg font-bold">Google Oturumu Doğrulanıyor</h2>
            <p className="text-sm text-slate-400">{state.message}</p>
          </div>
        )}
        {state.status === "pending" && (
          <div className="flex flex-col items-center text-center gap-4" data-testid="oauth-pending">
            <div className="w-12 h-12 border border-amber-500/40 bg-amber-500/10 flex items-center justify-center">
              <ShieldCheck size={22} className="text-amber-400" strokeWidth={1.5} />
            </div>
            <h2 className="font-heading text-lg font-bold">Onay Bekleniyor</h2>
            <p className="text-sm text-slate-400 leading-relaxed">{state.message}</p>
            <button
              className="btn btn-secondary w-full justify-center mt-2"
              onClick={() => navigate("/login", { replace: true })}
              data-testid="oauth-back-to-login"
            >
              Giriş sayfasına dön
            </button>
          </div>
        )}
        {state.status === "error" && (
          <div className="flex flex-col items-center text-center gap-4" data-testid="oauth-error">
            <div className="w-12 h-12 border border-red-500/40 bg-red-500/10 flex items-center justify-center">
              <AlertTriangle size={22} className="text-red-400" strokeWidth={1.5} />
            </div>
            <h2 className="font-heading text-lg font-bold">Giriş Başarısız</h2>
            <p className="text-sm text-slate-400 leading-relaxed">{state.message}</p>
            <button
              className="btn btn-primary w-full justify-center mt-2"
              onClick={() => navigate("/login", { replace: true })}
              data-testid="oauth-error-retry"
            >
              Tekrar Dene
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
